import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "contexts/i18nContext";
import React, { ChangeEvent, useState } from "react";
import { editAccusedSummon } from "services/prosecutorDecipilineService";
import { AccusedSummon, Complaints } from "types";
import { LoadingIcon } from "utils/constant";
import { ACCUSED_COMPLAINT_STATUS } from "utils/constantValues";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: AccusedSummon;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddSummonRegistrationModal({
  isOpen,
  setIsOpen,
  summonData,
  complaintData,
  onRefresh,
}: ModalProps) {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    registration_number: "",
    date: "",
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!summonData) {
      showToast(t("summonNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();
    data.append("registration_number", formState?.registration_number);
    data.append("registration_date", formState?.date);
    data.append("status", ACCUSED_COMPLAINT_STATUS.APPROVED);
    data.append(
      "accused_prosecutor_name",
      `${complaintData?.accused_prosecutor_name || ""}`,
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "assigned_prosecutor",
      complaintData?.assigned_prosecutor || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_assigned_prosecutor || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editAccusedSummon(data, summon_id, isFormData);

      onRefresh();
      showToast(t("successfullyConfirmed"), "success");
      setIsOpen(false);
      setFormState({
        registration_number: "",
        date: "",
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addSummonRegistrationNo")}
    >
      <form onSubmit={handleSubmit} className="victim-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("date")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="date"
              placeholder={t("datePlaceholder")}
              value={formState.date ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("registrationNo")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type="text"
              name="registration_number"
              placeholder={t("registrationPlaceholder")}
              value={formState.registration_number}
              onChange={handleChange}
            />
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("submit")
          )}
        </button>
      </form>
    </SideModal>
  );
}
